import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { getFollowMeSharedBetsFeed } from '@core/Areas/FollowMe/helpers/api'
import type { FollowMeFeedParams } from '@core/Areas/FollowMe/helpers/api/types'
import { ShareFeedPageSize } from '@core/Areas/FollowMe/constants'
import { useSharedBetsTopicSubscription } from '@core/Areas/FollowMe/hooks/useSharedBetsTopicSubscription'

export const useSharedBetsData = ({
  feedType = 'explore',
  sortOrder,
  sortFilter,
  accountId = null,
  pageSize,
}: Pick<
  FollowMeFeedParams,
  'feedType' | 'sortOrder' | 'sortFilter' | 'accountId' | 'pageSize'
>) => {
  useSharedBetsTopicSubscription()

  const { data, isLoading, refetch, isRefetching, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        ...queryKeys.followMeSharedBetsFeedBase(feedType),
        sortOrder,
        sortFilter,
        accountId,
        pageSize,
      ],

      queryFn: ({ pageParam }: { pageParam: number | null }) =>
        getFollowMeSharedBetsFeed({
          feedType,
          pageParam,
          sortOrder,
          sortFilter,
          accountId,
          pageSize: pageSize ?? ShareFeedPageSize,
        }),

      initialPageParam: null,

      getNextPageParam: lastPage => {
        if (!lastPage || lastPage.length < Number(pageSize ?? ShareFeedPageSize)) return undefined
        return lastPage[lastPage.length - 1]?.postId ?? undefined
      },

      staleTime: 1_000 * 60 * 2, // 2min
      gcTime: 1_000 * 60 * 5, // 5min
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    })

  const isEmptyState = data?.pages[0].length === 0
  const isFinalPage =
    data && data.pages[data.pages.length - 1].length < (pageSize ?? ShareFeedPageSize)

  return {
    data,
    isLoading,
    refetch,
    isRefetching,
    /** Automatically triggers a fetch for next page when in view */
    LoadMoreButton: () =>
      isFinalPage || isEmptyState || isLoading ? null : (
        <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
      ),
    isEmptyState,
  }
}
